/**
 * EL ITEM ESTANDAR DE MADERINCO, LEIDO Y EDITADO (ITEM-ESTANDAR-EDITOR-T1, Ares #43287, Josse 3-nov).
 *
 * La base (nexo-api 785, funciones item_estandar_*) es la que manda: arma el nombre, el texto del cliente y frena lo que
 * no cabe en la plantilla de la linea. Aqui se repiten esas reglas SOLO para la vista previa del editor (el nombre al
 * instante mientras se elige); al guardar, el nombre que se muestra es el de la respuesta. Sin DOM: lo prueba
 * scripts/check-item-estandar.mjs contra las mismas fichas v01 (sha 35b7827c).
 *
 * @module application/presupuestos/itemEstandar
 */

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());
const lista = (v) => (Array.isArray(v) ? v : []);
const de = (o, ...k) => { for (const x of k) if (o && o[x] !== undefined && o[x] !== null) return o[x]; return undefined; };
const numero = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** item_estandar_es_de_la_obra: ubicacion, cantidad y area son de la obra, no del item. */
export const esDeLaObra = (nombre) => /^(ubicaci|cantidad|[áa]rea( |$))/i.test(texto(nombre));
const esIncluye = (nombre) => /^incluye/i.test(texto(nombre));
const NUMERO_VALIDO = /^[0-9]+([.,][0-9]+)?$/;

function plantillaDe(p) {
  return {
    tipos: lista(de(p, 'tipos')).map(texto).filter(Boolean),
    caracteristicas: lista(de(p, 'caracteristicas')).map((c) => ({
      nombre: texto(de(c, 'nombre')),
      control: texto(de(c, 'control')) || 'lista',
      unidad: typeof de(c, 'unidad') === 'string' ? de(c, 'unidad') : null,
      obra: de(c, 'obra') === true || esDeLaObra(de(c, 'nombre')),
      opciones: lista(de(c, 'opciones')).map(texto)
    })).filter((c) => c.nombre),
    unidadCobro: texto(de(p, 'unidad_cobro', 'unidadCobro')) || null
  };
}

function caracteristicasDe(c) {
  const out = {};
  for (const [k, v] of Object.entries(c && typeof c === 'object' ? c : {})) out[k] = lista(v).map(texto).filter(Boolean);
  return out;
}

function estandarDe(e) {
  return {
    id: texto(de(e, 'id')),
    tipo: texto(de(e, 'tipo')) || null,
    nombre: texto(de(e, 'nombre')),
    // nexo-api 795 (migracion 029): nombre = coalesce(nombre_manual, nombre_armado); el escrito a mano o null.
    nombreManual: texto(de(e, 'nombre_manual', 'nombreManual')) || null,
    nombreArmado: texto(de(e, 'nombre_armado', 'nombreArmado')) || null,
    textoCliente: texto(de(e, 'texto_cliente', 'textoCliente')),
    // MAD-NOMBRE-PARAMETROS-DESCRIPCION-T1: la descripcion para el cliente escrita a mano (pedida a A en #44241); null hasta que la publique.
    descripcionManual: texto(de(e, 'descripcion_manual', 'descripcionManual')) || null,
    // nexo-api 802 (migracion 034): la generada (con el precio base) y si la API ya sabe guardar la escrita a mano.
    descripcionGenerada: texto(de(e, 'descripcion_generada', 'descripcionGenerada')) || null,
    aceptaDescripcion: de(e, 'descripcion_generada', 'descripcionGenerada') !== undefined,
    caracteristicas: caracteristicasDe(de(e, 'caracteristicas')),
    unidad: texto(de(e, 'unidad')) || null,
    incluye: lista(de(e, 'incluye')).map(texto),
    precio: numero(de(e, 'precio')),
    vigenteDesde: texto(de(e, 'vigente_desde', 'vigenteDesde')) || null,
    precioRefMin: numero(de(e, 'precio_ref_min', 'precioRefMin')),
    precioRefMax: numero(de(e, 'precio_ref_max', 'precioRefMax')),
    revision: numero(de(e, 'revision')),
    items: lista(de(e, 'items')).map((i) => ({
      itemId: texto(de(i, 'item_id', 'itemId')), ref: texto(de(i, 'ref')), descripcion: texto(de(i, 'descripcion')),
      unidad: texto(de(i, 'unidad')), precioRef: numero(de(i, 'precio_ref', 'precioRef')), faltan: lista(de(i, 'faltan')).map(texto)
    }))
  };
}

/**
 * La respuesta de GET plantilla/item-estandar normalizada (snake_case de la base o camelCase), o null si no trae lineas.
 * Acepta la respuesta del ApiBase ({ ok, data }) o el cuerpo ya leido.
 */
export function leerItemEstandar(res) {
  const cuerpo = res && typeof res === 'object' && 'ok' in res ? res.data : res;
  const d = cuerpo && typeof cuerpo === 'object' && cuerpo.data && !Array.isArray(cuerpo.data) ? cuerpo.data : cuerpo;
  if (!d || typeof d !== 'object') return null;
  const capitulos = lista(de(d, 'capitulos')).map((c) => ({
    capitulo: numero(de(c, 'capitulo')),
    nombre: texto(de(c, 'nombre')),
    lineas: lista(de(c, 'lineas')).map((l) => ({
      n: numero(de(l, 'n')),
      linea: texto(de(l, 'linea')),
      plantilla: plantillaDe(de(l, 'plantilla') || {}),
      preguntas: lista(de(l, 'preguntas')).map((p) => (typeof p === 'string' ? p : texto(de(p, 'pregunta', 'texto')))).filter(Boolean),
      nota: texto(de(l, 'nota')),
      // MAD-TIPO-TARJETA-APARIENCIA-T1: el tono guardado por tipo ({ tipo: clave de la paleta }); vacio hasta que A lo sirva.
      tonos: (() => { const t = de(l, 'tonos', 'Tonos'); return t && typeof t === 'object' && !Array.isArray(t) ? { ...t } : {}; })(),
      estandar: lista(de(l, 'estandar')).map(estandarDe)
    }))
  }));
  if (!capitulos.some((c) => c.lineas.length)) return null;
  const cu = de(d, 'cuentas') || {};
  return {
    negocio: texto(de(d, 'negocio')),
    cuentas: { items: numero(cu.items) ?? 0, asignados: numero(cu.asignados) ?? 0, porClasificar: numero(de(cu, 'por_clasificar', 'porClasificar')) ?? 0, estandar: numero(cu.estandar) ?? 0 },
    capitulos,
    porClasificar: lista(de(d, 'por_clasificar', 'porClasificar')).map((s) => ({
      seccion: texto(de(s, 'seccion')) || 'sin seccion',
      cuantos: numero(de(s, 'cuantos')) ?? lista(de(s, 'items')).length,
      items: lista(de(s, 'items')).map((i) => ({ itemId: texto(de(i, 'item_id', 'itemId')), ref: texto(de(i, 'ref')), descripcion: texto(de(i, 'descripcion')), unidad: texto(de(i, 'unidad')), precioRef: numero(de(i, 'precio_ref', 'precioRef')) }))
    }))
  };
}

// Los valores de una caracteristica en orden, como string_agg(v ORDER BY v) de la base.
const ordenados = (vals) => [...lista(vals)].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));

/**
 * item_estandar_nombre (nexo-api 804, migracion 036; antes 028 y 034): la linea es el grupo, no el nombre (DECISION
 * #44415). La cabeza es el tipo; sin tipo, o con un tipo que repite un valor elegido, no hay cabeza. Despues cada valor
 * en el orden de la plantilla, con su unidad si es numero, sin lo que incluye, sin el precio (034: va en la descripcion),
 * sin lo que no se eligio y sin repetir la cabeza. Solo si no queda nada (sin tipo ni caracteristicas) el nombre es la
 * linea. Vista previa: el que manda es el que devuelve la base.
 */
export function nombreArmado(linea, tipo, plantilla, caracteristicas, distinguen = null) {
  const car = caracteristicas || {};
  const igual = (a, b) => texto(a).toLowerCase() === texto(b).toLowerCase();
  // nexo-api 805 (037): con distinguen, solo las caracteristicas que distinguen al item en su linea.
  const usadas = (plantilla?.caracteristicas || [])
    .filter((c) => Object.prototype.hasOwnProperty.call(car, c.nombre) && !esIncluye(c.nombre) && !/^precio/i.test(texto(c.nombre))
      && (!Array.isArray(distinguen) || distinguen.includes(c.nombre)));
  const cabeza = !texto(tipo) || usadas.some((c) => lista(car[c.nombre]).some((v) => igual(v, tipo))) ? '' : texto(tipo);
  const partes = usadas.filter((c) => lista(car[c.nombre]).length)
    .map((c) => ordenados(car[c.nombre]).join(' y ') + (c.control === 'numero' && typeof c.unidad === 'string' ? ' ' + c.unidad : ''))
    .filter((p) => p && !igual(p, cabeza));
  return [cabeza, partes.join(', ')].filter(Boolean).join(', ') || texto(linea);
}

/**
 * item_estandar_distinguen (nexo-api 805, migracion 037): las caracteristicas (sin lo que incluye ni el precio) que mas
 * separan a los items de la linea, contando el que se edita: puntaje = cuantos la tienen x cuantos valores distintos,
 * en ese orden y despues el de la plantilla; se toman una a una hasta que los nombres no se repitan o sean 3. Lo que
 * todos tienen igual no entra. otras = caracteristicas de los demas items vigentes de la linea.
 */
export function distinguenEnLaLinea(plantilla, otras, caracteristicas) {
  const items = [...lista(otras).map((c) => c || {}), caracteristicas || {}];
  const n = items.length;
  if (n < 2) return [];
  const tiene = (it, k) => Object.prototype.hasOwnProperty.call(it, k);
  const valor = (it, k) => (tiene(it, k) ? JSON.stringify(it[k]) : null);
  const cands = (plantilla?.caracteristicas || [])
    .map((c, o) => ({ nombre: c.nombre, o }))
    .filter((c) => !esIncluye(c.nombre) && !/^precio/i.test(texto(c.nombre)))
    .filter((c) => new Set(items.map((it) => valor(it, c.nombre) ?? '[]')).size > 1)
    .map((c) => {
      const con = items.filter((it) => tiene(it, c.nombre));
      return { ...c, puntaje: con.length * new Set(con.map((it) => valor(it, c.nombre))).size };
    })
    .sort((a, b) => b.puntaje - a.puntaje || a.o - b.o);
  const elegidas = [];
  for (const c of cands) {
    elegidas.push(c.nombre);
    const orden = [...elegidas].sort();
    const distintos = new Set(items.map((it) => orden.map((e) => (tiene(it, e) ? lista(it[e]).join(',') : '')).join('|'))).size;
    if (distintos === n || elegidas.length >= 3) break;
  }
  return elegidas;
}

/** El nombre armado de un borrador en su linea, como la base (037): solo lo que lo distingue de sus hermanos. */
export function armadoEnLaLinea(linea, borrador) {
  const otras = (linea?.estandar || []).filter((e) => !borrador?.id || e.id !== borrador.id).map((e) => e.caracteristicas);
  return nombreArmado(linea?.linea, borrador?.tipo, linea?.plantilla, borrador?.caracteristicas,
    distinguenEnLaLinea(linea?.plantilla, otras, borrador?.caracteristicas));
}

/** Lo que incluye: los valores de las caracteristicas que empiezan por "Incluye", ordenados. */
export function incluyeDe(caracteristicas) {
  return ordenados(Object.entries(caracteristicas || {}).filter(([k]) => esIncluye(k)).flatMap(([, v]) => lista(v)));
}

/** item_estandar_texto: la linea, el nombre y lo que incluye. */
export function textoCliente(linea, nombre, incluye) {
  const inc = lista(incluye);
  return `${linea}: ${nombre}${inc.length ? '. Incluye ' + inc.join(', ') : ''}.`;
}

/** item_estandar_fallas: lo que la base frenaria, para decirlo antes de mandar. [] = nada. */
export function fallasDe(plantilla, caracteristicas) {
  const out = [];
  for (const [k, v] of Object.entries(caracteristicas || {})) {
    const c = (plantilla?.caracteristicas || []).find((x) => x.nombre === k);
    if (!c) out.push('CARACTERISTICA_DESCONOCIDA: ' + k);
    else if (c.obra) out.push('DE_LA_OBRA: ' + k + ' no entra al item');
    else if (!Array.isArray(v) || v.length === 0) out.push('VALOR_MAL_FORMADO: ' + k);
    else if (v.some((x) => typeof x !== 'string' || (!c.opciones.includes(x) && !(c.control === 'numero' && NUMERO_VALIDO.test(x))))) out.push('VALOR_FUERA_DE_LA_PLANTILLA: ' + k);
  }
  return out.sort();
}

/** La frase para el usuario de una falla de la base (sin el codigo). */
export function fraseDeFalla(f) {
  const [codigo, resto = ''] = String(f).split(': ');
  const que = resto.replace(' no entra al item', '');
  return {
    CARACTERISTICA_DESCONOCIDA: `"${que}" no es una característica de esta línea.`,
    DE_LA_OBRA: `"${que}" es de la obra (va en el presupuesto, no en el ítem).`,
    VALOR_MAL_FORMADO: `"${que}" no tiene valor.`,
    VALOR_FUERA_DE_LA_PLANTILLA: `"${que}" tiene un valor que no está entre sus opciones.`,
    TIPO_DESCONOCIDO: 'Ese tipo no es de esta línea.'
  }[codigo] || String(f);
}

/**
 * Cambia una caracteristica de un borrador y devuelve el borrador nuevo (no toca el de entrada). Lista: un valor; casillas:
 * los marcados; numero: el texto escrito. Vacio = la quita.
 */
export function conCaracteristica(borrador, nombre, valores) {
  const car = { ...(borrador?.caracteristicas || {}) };
  const v = lista(valores).map(texto).filter(Boolean);
  if (v.length) car[nombre] = v; else delete car[nombre];
  return { ...borrador, caracteristicas: car };
}

/**
 * EDITOR-ITEM-DISENO-T1 (maqueta 1793770085-ef18): las caracteristicas del editor en dos grupos, sin las de la obra.
 * Arriba lo que distingue a las opciones de la linea y lo que el item ya tiene; lo demas va en "Mas caracteristicas".
 */
export function caracteristicasDelEditor(plantilla, caracteristicas, distingue = []) {
  const cars = lista(plantilla?.caracteristicas).filter((c) => !c.obra && !esDeLaObra(c.nombre));
  const va = (c) => lista(distingue).includes(c.nombre) || lista(caracteristicas?.[c.nombre]).length > 0;
  return { arriba: cars.filter(va), mas: cars.filter((c) => !va(c)) };
}

/** Tocar un chip: con varios (casillas) suma o quita el valor; si no, lo deja solo; el vacio ("No lleva") lo quita. */
export function alternarValor(borrador, nombre, valor, varios = false) {
  const v = texto(valor);
  if (!v) return conCaracteristica(borrador, nombre, []);
  const actual = lista(borrador?.caracteristicas?.[nombre]);
  if (!varios) return conCaracteristica(borrador, nombre, [v]);
  return conCaracteristica(borrador, nombre, actual.includes(v) ? actual.filter((x) => x !== v) : [...actual, v]);
}

/**
 * El precio escrito en el editor: un precio nuevo queda vigente desde hoy; volver al de antes conserva su fecha; vacio
 * queda por confirmar (sin precio ni vigencia). original = el estandar que se edita.
 */
export function conPrecio(borrador, precio, original, hoy) {
  const p = numero(precio);
  if (p === null) return { ...borrador, precio: null, vigenteDesde: null };
  const igual = numero(original?.precio) === p && texto(original?.vigenteDesde);
  return { ...borrador, precio: p, vigenteDesde: igual ? texto(original.vigenteDesde) : hoy };
}

/**
 * MAD-IMPLEMENTAR-DISENO-T1 grupo 4: el item estandar de cada linea del armador, por el texto de la linea (las fichas
 * usan el mismo texto que la lista del cliente). Map linea -> { plantilla, estandar }.
 */
export function estandarPorLinea(lectura) {
  const m = new Map();
  for (const c of lectura?.capitulos || []) for (const l of c.lineas) m.set(l.linea, { plantilla: l.plantilla, estandar: l.estandar });
  return m;
}

/**
 * Las filas de una linea del armador con su item estandar: cada estandar con el item de la plantilla que lo representa
 * (su ref = fuenteId del armador), los tipos que tienen filas (con cuantas) y los items de la linea sin estandar.
 */
export function filasDeLinea(items, est) {
  const filas = [];
  const usados = new Set();
  for (const e of est?.estandar || []) {
    const refs = new Set(e.items.map((x) => x.ref));
    const item = (items || []).find((it) => refs.has(it.fuenteId));
    if (!item) continue;
    filas.push({ estandar: e, item, tipo: e.tipo || '' });
    for (const it of items) if (refs.has(it.fuenteId)) usados.add(it.id);
  }
  const orden = ['', ...(est?.plantilla?.tipos || [])];
  const tipos = orden.map((tipo) => ({ tipo, cuantos: filas.filter((f) => f.tipo === tipo).length })).filter((t) => t.cuantos > 0);
  return { filas, tipos, sueltos: (items || []).filter((it) => !usados.has(it.id)) };
}

/** El tipo que se muestra: el elegido; si no, el de lo que ya esta puesto; si hay uno solo, ese; si no, ninguno (elegir). */
export function tipoAMostrar(elegido, { filas, tipos }, yaPuestos) {
  if (elegido !== undefined && elegido !== null) return elegido;
  const puesta = filas.find((f) => yaPuestos?.has(f.item.id));
  if (puesta) return puesta.tipo;
  return tipos.length === 1 ? tipos[0].tipo : null;
}

/**
 * MAD-LAPIZ-COMPLETO-T1 (maqueta CN3bqybMgFosfhZKxrMTQY, SI de Josse #43980): el nombre escrito en el editor. Vacio o
 * igual al armado queda null (manda el armado, como la base: nullif(btrim(nombre_manual), '')); otro texto queda a mano.
 */
export function conNombre(borrador, escrito, armado) {
  const t = texto(escrito);
  return { ...borrador, nombreManual: t && t !== texto(armado) ? t : null };
}

/** El nombre que queda: el escrito a mano o, si no hay, el armado con las caracteristicas. */
export function nombreQueQueda(linea, borrador) {
  return texto(borrador?.nombreManual) || armadoEnLaLinea(linea, borrador);
}

/**
 * MAD-ITEM-NUEVO-T1 (Josse 4-nov: "no hay ninguna forma de crear un item nuevo"): el borrador de un item desde cero en una
 * linea (sin tipo, caracteristicas ni precio; nombre armado hasta que se escriba otro) y el estandar vacio que necesita el
 * editor para la unidad (la de la plantilla de la linea: la base la pone al crear).
 */
export function nuevoEstandar() {
  return { id: null, nuevo: true, tipo: null, caracteristicas: {}, nombreManual: null, precio: null, vigenteDesde: null, revision: null, items: [] };
}
/**
 * El cuerpo de POST plantilla/item-estandar/nuevo (DECISION #44142): el de crear con nombreManual siempre (lo escrito o el
 * nombre armado: la ruta lo pide) para que la base cree tambien el item de la plantilla y salga como tarjeta.
 */
export function cuerpoDeNuevo(linea, borrador) {
  const v = cuerpoDeEstandar(linea, { ...borrador, id: null });
  if (!v.ok) return v;
  const nombre = (texto(borrador && borrador.nombreManual) || armadoEnLaLinea(linea, borrador)).slice(0, 300);
  if (!nombre) return { ok: false, fallas: ['SIN_NOMBRE'] };
  return { ok: true, cuerpo: { ...v.cuerpo, nombreManual: nombre } };
}

export function estandarVacio(linea) {
  return { id: null, tipo: null, caracteristicas: {}, nombre: '', nombreManual: null, unidad: (linea && linea.plantilla && linea.plantilla.unidadCobro) || null, precio: null, vigenteDesde: null, precioRefMin: null, revision: null, items: [] };
}

/** Duplicar para crear una variante: el mismo tipo y caracteristicas, sin id, sin precio ni items (es otro estandar). */
export function varianteDe(estandar) {
  return {
    id: null, tipo: estandar?.tipo ?? null, nombreManual: null,
    caracteristicas: JSON.parse(JSON.stringify(estandar?.caracteristicas || {})),
    precio: null, vigenteDesde: null, revision: null, items: [], deDonde: estandar?.id || null
  };
}

/**
 * El cuerpo para guardar o crear con el contrato que acepto A (#43355), validado como lo valida la base. Cada valor va
 * como arreglo y "Incluye" va dentro de caracteristicas. Crear (POST, sin id): { linea, tipo, caracteristicas }. Editar
 * (PUT, con id): { tipo, caracteristicas, precio, vigenteDesde, revision }. Los dos con nombreManual (nexo-api 795). Devuelve { ok, cuerpo } o { ok: false, fallas }.
 */
export function cuerpoDeEstandar(linea, borrador) {
  const fallas = fallasDe(linea?.plantilla, borrador?.caracteristicas);
  if (borrador?.tipo && !(linea?.plantilla?.tipos || []).includes(borrador.tipo)) fallas.push('TIPO_DESCONOCIDO: ' + borrador.tipo);
  const precio = numero(borrador?.precio);
  if (precio !== null && !texto(borrador?.vigenteDesde)) fallas.push('PRECIO_SIN_VIGENCIA');
  if (fallas.length) return { ok: false, fallas };
  // nombreManual va siempre: la base guarda lo que llega (null = el armado), asi que no mandarlo borraria el escrito.
  const comun = { tipo: borrador.tipo || null, caracteristicas: borrador.caracteristicas || {}, nombreManual: texto(borrador.nombreManual) || null };
  // MAD-ITEM-NUEVO-T1: un item nuevo puede nacer con su precio (ItemEstandarCrearRequest.Precio y VigenteDesde).
  if (!borrador.id) return { ok: true, cuerpo: precio === null ? { linea: linea.n, ...comun } : { linea: linea.n, ...comun, precio, vigenteDesde: texto(borrador.vigenteDesde) } };
  const put = { ...comun, precio, vigenteDesde: precio === null ? null : texto(borrador.vigenteDesde), revision: borrador.revision ?? null };
  // MAD-NOMBRE-PARAMETROS-DESCRIPCION-T1 (nexo-api 802): el PUT guarda lo que llega en descripcionManual (null = la
  // generada), asi que va siempre que la API la acepte: no mandarla borraria la escrita al cambiar solo el precio.
  if (borrador.aceptaDescripcion) put.descripcionManual = texto(borrador.descripcionManual) || null;
  return { ok: true, cuerpo: put };
}

/**
 * Lo que dice la pantalla cuando la API no guarda (nexo-api 788, #43434). La API responde ProblemDetails con su codigo,
 * que PresupuestosApiClient.clasificar deja en error.code con el detalle: ITEM_ESTANDAR_REVISION (409, la revision ya no
 * es la actual), ITEM_ESTANDAR_REPETIDO (409, ya hay uno igual), ITEM_ESTANDAR_INVALIDO (422, el detalle trae las fallas
 * de la base), ITEM_ESTANDAR_NO_EXISTE (404). Sin codigo, por el estado HTTP; la ruta sin publicar dice "falta publicar".
 */
export function fraseDelGuardado(r, borrador = null) {
  if (r?.ok) return texto(borrador?.nombreManual) ? 'Guardado con el nombre que escribiste.' : 'Guardado. El nombre que queda es el que armó la base.';
  const codigo = r?.error?.code;
  const detalle = texto(r?.error?.detalle ?? r?.details?.detail);
  if (codigo === 'NOT_IMPLEMENTED') return 'La API todavía no publica guardar el ítem estándar: lo que elegiste sigue en pantalla.';
  if (codigo === 'ITEM_ESTANDAR_REVISION' || (!codigo && r?.code === 409)) return 'Alguien cambió este estándar mientras lo editabas: ciérralo y ábrelo otra vez.';
  if (codigo === 'ITEM_ESTANDAR_REPETIDO') return 'Ya hay un estándar igual en esta línea: búscalo en la lista en vez de crear otro.';
  if (codigo === 'ITEM_ESTANDAR_NO_EXISTE') return 'Ese estándar ya no existe: vuelve a abrir la línea.';
  if (codigo === 'ITEM_ESTANDAR_INVALIDO' || (!codigo && r?.code === 422)) {
    // La base dice sus fallas con su codigo (CODIGO: que), separadas por '; '.
    const fallas = detalle.replace(/^ITEM_ESTANDAR:\s*/, '').split(/;\s*/).filter(Boolean);
    return 'La base no lo acepta: ' + (fallas.length ? fallas.map(fraseDeFalla).join(' ') : 'revisa las características.');
  }
  return typeof r?.error === 'string' && r.error ? r.error : 'No se guardó. Intenta de nuevo en un momento.';
}

/**
 * MAD-ELIMINAR-ITEM-T1 (nexo-api 795): cuantos presupuestos usan el estandar, de GET {id}/uso ({ presupuestos }), o null.
 */
export function usoDe(res) {
  const d = res && typeof res === 'object' && 'ok' in res ? res.data : res;
  const p = d && typeof d === 'object' && d.data && typeof d.data === 'object' ? d.data : d;
  return numero(p && de(p, 'presupuestos', 'Presupuestos'));
}

/** La pregunta antes de borrar: si lo usan, se archiva (los presupuestos lo conservan); si no, se elimina. */
export function preguntaDeEliminar(nombre, presupuestos) {
  const n = numero(presupuestos) ?? 0;
  const que = `"${texto(nombre)}"`;
  if (n > 0) {
    return { accion: 'archivar', boton: 'Sí, archivar',
      texto: `¿Eliminar ${que}? Lo usa${n === 1 ? '' : 'n'} ${n} presupuesto${n === 1 ? '' : 's'} guardado${n === 1 ? '' : 's'}: se archiva. Deja de salir aquí y en el armador; ${n === 1 ? 'ese presupuesto lo conserva' : 'esos presupuestos lo conservan'} con su precio.` };
  }
  return { accion: 'eliminar', boton: 'Sí, eliminar',
    texto: `¿Eliminar ${que}? Ningún presupuesto lo usa: se elimina, y los ítems de la plantilla que tenía vuelven a Por clasificar.` };
}

/** Lo que dice la lista despues de borrar, con la respuesta de DELETE ({ eliminado, archivado, presupuestos, items_por_clasificar }). */
export function fraseDelEliminado(nombre, res) {
  const d = res && typeof res === 'object' && 'ok' in res ? res.data : res;
  const r = d && typeof d === 'object' && d.data && typeof d.data === 'object' ? d.data : (d || {});
  const n = numero(de(r, 'presupuestos')) ?? 0;
  const vuelven = numero(de(r, 'items_por_clasificar', 'itemsPorClasificar')) ?? 0;
  if (de(r, 'archivado') === true) return `Archivado: ${texto(nombre)}. ${n === 1 ? 'El presupuesto que lo usa lo conserva' : `Los ${n} presupuestos que lo usan lo conservan`} con su precio.`;
  return `Eliminado: ${texto(nombre)}.${vuelven ? ` ${vuelven} ítem${vuelven === 1 ? '' : 's'} de la plantilla vuelve${vuelven === 1 ? '' : 'n'} a Por clasificar.` : ''}`;
}

/** El precio que se muestra: el confirmado o, si no hay, la referencia minima de la plantilla (y se marca por confirmar). */
export function precioDeEstandar(e) {
  if (e?.precio !== null && e?.precio !== undefined) return { valor: e.precio, porConfirmar: false };
  return { valor: e?.precioRefMin ?? null, porConfirmar: true };
}

const sinTildes = (t) => texto(t).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * CATALOGO-UNICO-ITEMS-T1 (maqueta aprobada, Ares #43601): "Tus items y precios" es el catalogo del item estandar.
 * filtro: 'todos' | 'por_confirmar' (sin precio confirmado) | 'por_clasificar'; buscar en nombre, tipo y linea, sin tildes.
 * Devuelve las cuentas de los tres filtros (no cambian con la busqueda) y las secciones con sus lineas y estandar visibles.
 */
export function catalogoDeItems(lectura, { filtro = 'todos', buscar = '' } = {}) {
  const caps = lista(lectura?.capitulos);
  const todos = caps.flatMap((c) => c.lineas.flatMap((l) => l.estandar));
  const cuentas = {
    todos: todos.length,
    porConfirmar: todos.filter((e) => precioDeEstandar(e).porConfirmar).length,
    porClasificar: lista(lectura?.porClasificar).reduce((n, s) => n + (Number(s.cuantos) || 0), 0)
  };
  if (filtro === 'por_clasificar') return { cuentas, secciones: [], porClasificar: lista(lectura?.porClasificar) };
  const q = sinTildes(buscar);
  const secciones = caps.map((c) => ({
    capitulo: c.capitulo, nombre: c.nombre,
    lineas: c.lineas.map((l) => ({
      n: l.n, linea: l.linea,
      estandar: l.estandar.filter((e) => (filtro !== 'por_confirmar' || precioDeEstandar(e).porConfirmar)
        && (!q || sinTildes(`${e.nombre} ${e.tipo || ''} ${l.linea}`).includes(q)))
    })).filter((l) => l.estandar.length)
  })).filter((c) => c.lineas.length);
  return { cuentas, secciones, porClasificar: [] };
}
